// The books screens' pure helpers: money and dates as the office reads them, aging buckets, what an
// agent's money request is, period arithmetic. Kept apart from the screens so they can be tested.

import { dateText, dateTimeText } from '@flowology/kernel';

export const BUCKETS = ['current', 'days_1_30', 'days_31_60', 'days_61_90', 'days_over_90'] as const;
export type Bucket = (typeof BUCKETS)[number];
export const BUCKET_LABEL: Record<Bucket, string> = {
  current: 'Not due yet',
  days_1_30: '1–30 days',
  days_31_60: '31–60 days',
  days_61_90: '61–90 days',
  days_over_90: '90+ days',
};

const dollars = new Intl.NumberFormat('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** "$1,234.56", "−$86.12" (a true minus sign); signed amounts can lead with "+". */
export function money(cents: number, opts: { signed?: boolean } = {}): string {
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const text = `$${dollars.format(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, '0')}`;
  return neg ? `−${text}` : opts.signed && cents > 0 ? `+${text}` : text;
}

/** Money split for display: the dollars large, the cents small ("$1,234" + ".56"). */
/** Which accounts the cash is in, and the money on its way that isn't in them yet (QA-340). */
export function cashNote(b: { bankAccounts: { name: string }[]; undepositedCents: number }): string {
  const names = b.bankAccounts.map((x) => x.name).join(' · ');
  if (b.undepositedCents <= 0) return names;
  const coming = `${money(b.undepositedCents)} more on its way: card payments not paid out yet, cash and cheques not deposited`;
  return names ? `${names}. ${coming}` : coming;
}

export function moneyParts(cents: number): { sign: string; whole: string; cents: string } {
  const m = money(cents);
  const sign = m.startsWith('−') ? '−' : '';
  const [whole, c] = m.replace('−', '').split('.') as [string, string];
  return { sign, whole, cents: `.${c}` };
}

/** "$12.34" as typed → cents; null when it isn't a money amount. */
export function parseMoney(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '').replace('−', '-');
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  const [w, f = ''] = t.replace('-', '').split('.') as [string, string?];
  const cents = Number(w) * 100 + Number((f + '00').slice(0, 2));
  return t.startsWith('-') ? -cents : cents;
}

/** "Oct 20" (this year) or "Oct 20, 2025". */
export function dayText(iso: string, thisYear = new Date().getFullYear()): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    ...(d.getUTCFullYear() !== thisYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(d);
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The calendar month a date is in: [first day, last day]. */
export function monthOf(date: string): [string, string] {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return [`${y}-${mm}-01`, `${y}-${mm}-${String(last).padStart(2, '0')}`];
}

/** The month before. */
export function previousMonth(date: string): [string, string] {
  return monthOf(addDays(monthOf(date)[0], -1));
}

export function monthName(date: string): string {
  return new Intl.DateTimeFormat('en-CA', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${date.slice(0, 10)}T12:00:00Z`),
  );
}

/** The first day of the month after a YYYY-MM-DD's. */
function nextMonthStart(date: string): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
}

/**
 * Where an entry dated in a closed month posts (Q48, F-37): the first open month after it, keeping
 * its own date. "Posts in October (September is closed)", with the years when they differ; null when
 * the date's month is open (or the date isn't one yet).
 */
export function closedMonthHint(
  date: string,
  periods: readonly { startDate: string; endDate: string; status: 'open' | 'closed' }[],
): string | null {
  const closed = (d: string) =>
    periods.some((p) => p.status === 'closed' && p.startDate <= d && d <= p.endDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !closed(date)) return null;
  let at = nextMonthStart(date);
  for (let i = 0; i < 240 && closed(at); i++) at = nextMonthStart(at);
  const name =
    at.slice(0, 4) === date.slice(0, 4)
      ? (d: string) =>
          new Intl.DateTimeFormat('en-CA', { month: 'long', timeZone: 'UTC' }).format(
            new Date(`${d}T12:00:00Z`),
          )
      : monthName;
  return `Posts in ${name(at)} (${name(date.slice(0, 10))} is closed)`;
}

/** The company's day (YYYY-MM-DD) a moment fell on: an evening's UTC timestamp is still today there. */
export function dayIn(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function todayIn(timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Hours from minutes: "7.5 h". */
export function hours(minutes: number): string {
  const h = Math.round((minutes / 60) * 100) / 100;
  return `${h % 1 === 0 ? h.toFixed(0) : h.toFixed(2).replace(/0$/, '')} h`;
}

// An agent's request that moves money needs the owner's grant for its kind to decide (Q46).
const MONEY_KIND: Record<string, 'payments' | 'refunds' | 'bank' | 'vendorBills'> = {
  'payments.record': 'payments',
  'payments.refund': 'refunds',
  'payments.refundPayment': 'refunds',
  'deposits.record': 'bank',
  'bank.match': 'bank',
  'bank.categorise': 'bank',
  'vendorBills.pay': 'vendorBills',
};
export const MONEY_KIND_LABEL = {
  payments: 'Customer payment',
  refunds: 'Refund',
  bank: 'Bank match',
  vendorBills: 'Vendor payment',
} as const;

export const moneyKindOf = (action: string) => MONEY_KIND[action] ?? null;

/** Books-related requests: the money ones plus the books' own (journal entries, reminders, rules…). */
export function isBooksRequest(action: string): boolean {
  return (
    moneyKindOf(action) !== null ||
    // A family of actions (bank.*, journal.*…), or the one collections-hold action (QA-259).
    /^((bank|journal|receivables|vendorBills|tax|credits)\.|customers\.setCollectionsHold$)/.test(action)
  );
}

/** "agent:accounting" → "Accounting agent"; a person's own AI tool ("user:…") → "An AI tool" (DEC-009). */
export function agentName(ref: string): string {
  if (ref.startsWith('user:')) return 'An AI tool';
  const key = ref.replace(/^agent:/, '');
  const names: Record<string, string> = {
    receptionist: 'Voice receptionist',
    copilot: 'Office copilot',
    tech: 'Tech assistant',
    accounting: 'Accounting agent',
    analyst: 'Owner analyst',
    support: 'Support agent',
  };
  return names[key] ?? key;
}

/** The invoice ids a request's input names (at its top or in its allocations), to show their numbers. */
export function invoiceIdsIn(input: unknown): string[] {
  const out = new Set<string>();
  const walk = (v: unknown) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object')
      for (const [k, x] of Object.entries(v as Record<string, unknown>))
        if (k === 'invoiceId' && typeof x === 'string') out.add(x);
        else if (typeof x === 'object') walk(x);
  };
  walk(input);
  return [...out];
}

/** "paidOn" → "Paid on", "vendorId" → "Vendor", "journalLineIds" → "Journal lines". */
const fieldLabel = (k: string) =>
  k
    .replace(/Ids$/, 's')
    .replace(/Id$/, '')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

/** How a payment method is said, as the screens that take one say it (Payables, Receivables). */
const METHOD_WORDS: Record<string, string> = {
  eft: 'EFT',
  pad: 'Pre-authorised debit',
  etransfer: 'e-transfer',
  cheque: 'cheque',
  card: 'card',
  cash: 'cash',
  other: 'other',
  financing: 'financing',
};
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** One line of a request's input: its words, and the exact value it runs with (F-04). */
export interface InputRow {
  label: string;
  text: string;
  exact: string;
}

/**
 * Plain-language summary of a request's exact input, field by field: money as money, days as
 * "Oct 15, 2026", a method or other code in words ("e-transfer"), invoices by number when known
 * ("$129.00 to INV-1042", QA-267), any other record by its name beside its id ("Island Plumbing
 * Supply (01a142b5…)", QA-357), and anything nested in words, never raw JSON. `names` maps an id to
 * its number or name; an id it doesn't know stays as it is. Each row keeps the exact value too.
 */
export function inputRows(
  input: unknown,
  names: ReadonlyMap<string, string> = new Map(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): InputRow[] {
  if (!input || typeof input !== 'object') return [];
  const invoice = (id: string) => names.get(id) ?? `invoice …${id.slice(-6)}`;
  const value = (k: string, v: unknown): string => {
    if (/Cents$/.test(k) && typeof v === 'number') return money(v);
    if (k === 'invoiceId' && typeof v === 'string') return invoice(v);
    if (/Ids?$/.test(k) && typeof v === 'string') {
      const name = names.get(v);
      return name ? `${name} (${v.slice(0, 8)}…)` : v;
    }
    if (typeof v === 'string' && /method$/i.test(k) && METHOD_WORDS[v]) return METHOD_WORDS[v];
    if (typeof v === 'string' && DAY.test(v)) return dateText(v, 'UTC');
    if (typeof v === 'string' && INSTANT.test(v) && !Number.isNaN(Date.parse(v)))
      return dateTimeText(v, timeZone);
    if (typeof v === 'string' && /^[a-z]+(_[a-z]+)+$/.test(v)) return v.replace(/_/g, ' '); // a code: "sold_later"
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    if (Array.isArray(v))
      return v.map((x) => value(k, x)).join(v.some((x) => x && typeof x === 'object') ? '; ' : ', ');
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.amountCents === 'number' && typeof o.invoiceId === 'string')
        return `${money(o.amountCents)} to ${invoice(o.invoiceId)}`;
      return (
        Object.entries(o)
          // a named record says what it is ("Bill #B-114 from …"), so its field's word isn't repeated
          .map(([kk, x]) =>
            /Ids?$/.test(kk) && typeof x === 'string' && names.has(x)
              ? value(kk, x)
              : `${fieldLabel(kk)
                  .replace(/ cents$/, '')
                  .toLowerCase()} ${value(kk, x)}`,
          )
          .join(', ')
      );
    }
    return String(v);
  };
  return Object.entries(input as Record<string, unknown>).map(([k, v]) => ({
    label: k === 'invoiceId' ? 'Invoice' : fieldLabel(k).replace(/ cents$/, ''),
    text: value(k, v),
    exact: typeof v === 'string' ? v : JSON.stringify(v),
  }));
}

/** The same rows as [label, words] pairs. */
export const inputLines = (
  input: unknown,
  names?: ReadonlyMap<string, string>,
  timeZone?: string,
): [string, string][] => inputRows(input, names, timeZone).map((r) => [r.label, r.text]);

/** Sum of a list's cents. */
/** Payroll is for whoever prepares it (payroll.prepare: the owner and the bookkeeper; Q42, QA-274). */
/** What posted a journal entry, in words: its posting rule, else its source ("invoice.finalized" → "Invoice"). */
const ENTRY_KIND: Record<string, string> = {
  'bank.categorise': 'Bank line',
  'credit_note.issued': 'Credit note',
  'customer_credit.applied': 'Credit applied',
  'customer_credit.released': 'Credit released',
  'deposit.settled': 'Deposit',
  'invoice.finalized': 'Invoice',
  'invoice.written_off': 'Write-off',
  'journal.post': 'Journal entry',
  'membership_visit.recognised': 'Membership visit',
  'payment.disputed': 'Dispute',
  'payment.recorded': 'Payment',
  'payroll.funded': 'Payroll funding',
  'payroll.remitted': 'Payroll remittance',
  'payroll.run_posted': 'Payroll',
  'po.received': 'Stock received',
  'refund.issued': 'Refund',
  'stock.count_adjusted': 'Stock count',
  'stock.used_on_job': 'Materials used',
  'tax.remitted': 'Tax payment',
  'vendor_bill.approved': 'Bill',
  'vendor_payment.made': 'Bill payment',
  manual: 'Journal entry',
  import: 'Imported',
  reversal: 'Reversal',
  system: 'System',
};
export function entryKind(code: string): string {
  const known = ENTRY_KIND[code];
  if (known) return known;
  const words = code.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Who opens Payroll: payroll.view (the owner's, or a role of theirs that gives it) or payroll.prepare (the bookkeeper). */
/** Who enters bills and vendors (ap.manage): the owner, the manager and the bookkeeper. */
export const AP_PERMS = ['ap.manage'];

export const seesPayroll = (held: readonly string[]) =>
  held.includes('payroll.view') || held.includes('payroll.prepare');

export const total = <T>(xs: readonly T[], f: (x: T) => number) => xs.reduce((s, x) => s + f(x), 0);

/**
 * One payment across a customer's open invoices (F-07), each up to its balance. Opened from an
 * invoice (firstId), that one first: the person pressed Record payment on it, and the cheque usually
 * names it. Then the rest, oldest due first; opened from the customer, oldest due first throughout.
 * What is left is an unapplied credit for a person to apply later (Q29), never applied for them.
 */
export function spreadPayment(
  amountCents: number,
  invoices: readonly { invoiceId: string; dueOn: string; balanceCents: number }[],
  firstId?: string,
): { allocations: { invoiceId: string; amountCents: number }[]; creditCents: number } {
  const order = [...invoices].sort(
    (a, b) =>
      Number(b.invoiceId === firstId) - Number(a.invoiceId === firstId) ||
      a.dueOn.localeCompare(b.dueOn) ||
      a.invoiceId.localeCompare(b.invoiceId),
  );
  let left = Math.max(0, amountCents);
  const allocations = order.map((i) => {
    const take = Math.min(left, Math.max(0, i.balanceCents));
    left -= take;
    return { invoiceId: i.invoiceId, amountCents: take };
  });
  return { allocations, creditCents: left };
}

/**
 * Asked before a payment that is more than its invoices take is saved (QA-716): the rest is kept as
 * the customer's credit, so a typo (500 for 50) never becomes credit, and a receipt, without a look.
 * Null when it all goes on invoices.
 */
export function keepCreditAsk(
  receivedCents: number,
  appliedCents: number,
  owedCents: number,
  customerName: string,
): string | null {
  const credit = receivedCents - appliedCents;
  if (credit <= 0) return null;
  const than = appliedCents >= owedCents ? `${customerName} owes` : 'is applied to invoices';
  return `${money(receivedCents)} is ${money(credit)} more than ${than}. That ${money(credit)} will be kept as credit for ${customerName}, to apply later.`;
}

/**
 * The day a payment came in, as the office dates it (QA-715): today or earlier. The books refuse a
 * closed month themselves, in their own words.
 */
export function receivedOnProblem(receivedOn: string, today: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(receivedOn)) return 'Give the day it came in.';
  return receivedOn > today ? 'A payment can’t be received after today.' : null;
}

/**
 * Reads again until `done` (QA-716): what the books post follows an action a moment later, so a figure
 * read straight after it can still be the old one. Waits between reads, and gives the last read up
 * when they run out.
 */
export async function readUntil<T>(
  read: () => Promise<T>,
  done: (v: T) => boolean,
  waitsMs: readonly number[] = [500, 1000, 2000, 4000],
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<T> {
  let v = await read();
  for (const ms of waitsMs) {
    if (done(v)) return v;
    await sleep(ms);
    v = await read();
  }
  return v;
}

/**
 * Receivables' tie to the AR account, in words (QA-508): it ties; a posting just recorded is still
 * on its way to the ledger (not a difference, so it isn't called one); or it really differs, and if a
 * posting failed, that is said too.
 */
export function arTie(a: { differenceCents: number; postingsWaiting: number; postingsFailed: number }): {
  state: 'ties' | 'posting' | 'differs';
  failed: boolean;
} {
  if (a.differenceCents === 0) return { state: 'ties', failed: false };
  if (a.postingsWaiting > 0) return { state: 'posting', failed: false };
  return { state: 'differs', failed: a.postingsFailed > 0 };
}
